import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {NavLink} from 'react-router-dom';
import {FormattedMessage} from 'react-intl';
import cn from 'classnames';

import {Navigation} from './Navigation/Navigation';
import {ProfileMenu} from './profile-menu/ProfileMenu';
import {MobileDrawer} from './mobile-drawer/MobileDrawer';

import {startLoginAction} from 'redux/auth/authAsyncActions';
import {IStore} from 'redux/interface';
import {getNotificationCount, notificationAsyncActions} from 'redux/pageAsyncActions';
import {loadTranslation} from 'redux/translation/translationAsyncActions';

import Logo from 'assets/image/header/logo-2.png';
import LogoEs from 'assets/image/header/logo-2-es.png';
import LoginImg from 'assets/image/home-page/login.svg';
import UserIcon from 'assets/image/user-settings/userDefaultAvatar.png';
import HamburgerIcon from 'assets/image/mobile-nav/menu-hamburger.svg';

import {desktopWidth, SystemLanguage} from 'utils/constants/constants';
import {useResize} from 'utils/hooks/useResize';
import setIsBodyScrolling from 'utils/helpers/setIsBodyScrolling';
import {RoutesUrl, routeTranslationKeys} from 'utils/constants/routeConstants';
import {useLoadFile} from 'utils/hooks/useLoadFile';

import './styles.scss';
import {LanguageMenu} from './language-menu/LanguageMenu';
import {IContentNavItem, INavItem} from './interfaces';
import {navItems} from './constants';
import {getLocalizedTitle} from './utils';

function shouldCloseMobileMenus(screenWidth: number, isOpenAuthMenu: boolean, isDrawerOpen: boolean): boolean {
  return screenWidth >= desktopWidth && (isOpenAuthMenu || isDrawerOpen);
}

function canShowAuthedNav(auth0User: unknown, userProfile: {block: boolean} | null): boolean {
  return auth0User !== null && !!userProfile && !userProfile.block;
}

export const Header: React.FunctionComponent = () => {
  const {
    auth0User,
    userProfile,
    settings
  } = useSelector((state: IStore) => state.auth);
  const {
    translationKeys,
    locale
  } = useSelector((state: IStore) => state.translation);
  const notificationCount = useSelector((state: IStore) => state.notifications.notificationCount);

  const dispatch = useDispatch();
  const {
    getFileUrlAsync
  } = useLoadFile();

  const [isOpenAuthMenu, setIsOpenAuthMenu] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string>();

  const showAuthedNav = canShowAuthedNav(auth0User, userProfile);

  const screenWidth = useResize();

  const node = useRef<HTMLDivElement>(null);

  const getFileUrl = async (avatarId: number) => {
    const url = await getFileUrlAsync(avatarId);

    setAvatarUrl(url);
  };

  const allowedNavItems = useMemo<INavItem[]>(() => {
    if (!settings?.pages) {
      return [];
    }

    return settings.pages.filter(page => !!page.showInUserMenu).flatMap((page) => {
      const navItem = navItems.find(
        (item) => item.name.toLowerCase() === page.name.toLowerCase()
      );

      if (!navItem) {
        return [];
      }

      return {
        ...navItem,
        title: page.title || navItem.title,
        titles: page.titles,
        translationKey: navItem.title,
        role: page.role ?? navItem.role ?? null
      };
    });
  }, [settings?.pages]);

  const allowedContentNavItems = useMemo<IContentNavItem[]>(() => {
    if (!settings?.contentPages) {
      return [];
    }

    return settings.contentPages.filter(page => !!page.showInUserMenu).flatMap((page, i) => {
      return {
        index: i,
        path: `${RoutesUrl.Content}#${page.contentId}`,
        title: page.title,
        titles: page.titles,
        role: page.role ?? null
      };
    });
  }, [settings?.contentPages]);

  useEffect(() => {
    if (shouldCloseMobileMenus(screenWidth, isOpenAuthMenu, isDrawerOpen)) {
      setIsBodyScrolling(true);
      setIsOpenAuthMenu(false);
      setIsDrawerOpen(false);
    }
  }, [screenWidth]);

  useEffect(() => {
    if (userProfile) {
      if (userProfile.userAvatarId) {
        getFileUrl(userProfile.userAvatarId);
      } else {
        setAvatarUrl(UserIcon);
      }
    }
  }, [userProfile?.userAvatarId]);

  useEffect(() => {
    if (userProfile && !userProfile.block) {
      dispatch(notificationAsyncActions[getNotificationCount]());
    }
  }, [userProfile?.id]);

  useEffect(() => {
    console.log('notificationCount', notificationCount);
  }, [notificationCount]);

  const escapeListener = useCallback((event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      setIsOpenAuthMenu(false);
      setIsDrawerOpen(false);
    }
  }, []);

  const handleClick = useCallback((event: MouseEvent): void => {
    const element = event.target as HTMLElement;

    if (node.current && node.current.contains(element)) {
      return;
    }
    setIsOpenAuthMenu(false);
  }, []);

  useEffect(() => {
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keyup', escapeListener);

    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keyup', escapeListener);
    };
  }, []);

  async function handleSignIn() {
    dispatch(startLoginAction());
  }

  function handleSignOut() {
    if (isOpenAuthMenu) {
      setIsOpenAuthMenu(false);
    }
  }

  function handleMenuAuthButton() {
    setIsDrawerOpen(false);
    setIsOpenAuthMenu(!isOpenAuthMenu);
  }

  function handleScrollTop() {
    window.scrollTo(0, 0);
  }

  function handleCloseAuthMenu() {
    setIsOpenAuthMenu(false);
  }

  function handleOpenDrawer() {
    setIsOpenAuthMenu(false);
    setIsDrawerOpen(true);
  }

  function handleCloseDrawer() {
    setIsDrawerOpen(false);
  }

  function handleClickSettings() {
    if (!translationKeys.includes(routeTranslationKeys[RoutesUrl.Settings])) {
      dispatch(loadTranslation(routeTranslationKeys[RoutesUrl.Settings]));
    }
    setIsOpenAuthMenu(false);
  }

  function handleClickNavItem(route: RoutesUrl) {
    return () => {
      if (!translationKeys.includes(routeTranslationKeys[route])) {
        dispatch(loadTranslation(routeTranslationKeys[route]));
      }
      setIsOpenAuthMenu(false);
    };
  }

  function handleClickContentNavItem() {
    if (!translationKeys.includes(routeTranslationKeys[RoutesUrl.Content])) {
      dispatch(loadTranslation(routeTranslationKeys[RoutesUrl.Content]));
    }
    setIsOpenAuthMenu(false);
  }

  function showUserInitials() {
    return (
      (userProfile && userProfile.firstName && userProfile.lastName)
        ? `${userProfile.firstName.substr(0, 1)}${userProfile.lastName.substr(0, 1)}`
        : auth0User?.nickname.substr(0, 2)
    );
  }

  const ButtonSignIn = () => {
    return (
      <div className="header-button-wrapper">
        {auth0User === null ? (
          <>
            <LanguageMenu/>
            <button
              className="header-button default-signIn-btn"
              onClick={handleSignIn}
            >
              {/* <img className="signIn-btn-image" src={LoginImg} alt="" role="presentation"/>*/}
              <FormattedMessage
                id={'gritx.common.header.signIn'}
                defaultMessage={'Sign In'}
              />
            </button>
          </>
        ) : (
          <button
            className="header-button default-signIn-btn"
            onClick={handleSignOut}
          >
            {avatarUrl && <img
              className="signIn-btn-image"
              src={avatarUrl}
              alt=""
              role="presentation"
            />}
            <FormattedMessage
              id={'gritx.common.header.signOut'}
              defaultMessage={'Sign Out'}
            />
          </button>
        )}
      </div>
    );
  };

  const ButtonAuthUser = () => {
    return (
      <>
        <div className="header-button-wrapper">
          {auth0User !== null ? (
            <button
              className="default-auth-user"
              onClick={handleMenuAuthButton}
            >
              <p className="default-auth-user__name">
                {showUserInitials()}
              </p>
            </button>
          ) : null}
          {userProfile && !userProfile.block && settings?.profileSettings?.showNotifications && (
            <NavLink
              to={RoutesUrl.Notifications}
              className="header-bell"
              aria-label={`Notifications${notificationCount > 0 ? `, ${notificationCount} unread` : ''}`}
            >
              <svg
                className="header-bell__icon"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
              </svg>
              {notificationCount > 0 && (
                <span className="header-bell__badge" aria-hidden="true">
                  {notificationCount > 99 ? '99+' : notificationCount}
                </span>
              )}
            </NavLink>
          )}
        </div>
      </>
    );
  };

  const MenuAuthUser = () => {
    return (
      <ul className="menu-auth__items">
        {
          userProfile && !userProfile.block && allowedNavItems.map((item, index) => {
            if (item.role && !userProfile.roles.includes(item.role)) {
              return null;
            }

            return (<li key={item.index} className="menu-auth__item">
              <NavLink
                className="menu-auth__btn"
                to={item.path}
                onClick={handleClickNavItem(item.path)}
              >
                {getLocalizedTitle(item.title, item.titles, locale) || (
                  <FormattedMessage
                    id={`gritx.common.navigation.${item.translationKey || item.title}`}
                    defaultMessage={item.title}
                  />
                )}
              </NavLink>
            </li>);
          })
        }
        {
          userProfile && !userProfile.block && allowedContentNavItems.map((item, index) => {
            if (item.role && !userProfile.roles.includes(item.role)) {
              return null;
            }

            return (<li key={item.index} className="menu-auth__item">
              <NavLink
                className="menu-auth__btn"
                to={item.path}
                onClick={handleClickContentNavItem}
              >
                {getLocalizedTitle(item.title, item.titles, locale) || (
                  <FormattedMessage
                    id={`gritx.common.navigation.${item.title}`}
                    defaultMessage={item.title}
                  />
                )}
              </NavLink>
            </li>);
          })
        }
        {userProfile && !userProfile.block && <li className="menu-auth__item">
          <NavLink
            className="menu-auth__btn"
            to={RoutesUrl.Settings}
            onClick={handleClickSettings}
          >
            <FormattedMessage
              id={'gritx.common.header.myProfile'}
              defaultMessage={'My Profile'}
            />
          </NavLink>
        </li>}
        <li className="menu-auth__item">
          <NavLink
            to="/logout"
            className="menu-auth__btn"
            onClick={handleSignOut}
          >
            <FormattedMessage
              id={'gritx.common.header.logout'}
              defaultMessage={'Logout'}
            />
          </NavLink>
        </li>
      </ul>
    );
  };

  return (
    <header className="header">
      <div className="container" ref={node}>
        <div className="header__wrapper">
          <div className="header__left">
            {showAuthedNav && (
              <button
                type="button"
                className="header__hamburger"
                onClick={handleOpenDrawer}
                aria-label="Open menu"
              >
                <img src={HamburgerIcon} alt="" role="presentation"/>
              </button>
            )}
            <button className="header__logo" onClick={handleScrollTop}>
              <img
                className="header__logo-image"
                src={locale === SystemLanguage.Spanish ? LogoEs : Logo}
                alt="Logo"
              />
            </button>
            {auth0User === null
              ? <></>
              : userProfile && !userProfile.block && (
                <Navigation
                  className="nav__desktop"
                  onOpenMenu={handleCloseAuthMenu}
                  roles={userProfile.roles}
                />
              )}
          </div>
          <div className="header__right">
            {auth0User === null ? <ButtonSignIn/> : <ButtonAuthUser/>}
          </div>
        </div>

        {auth0User === null ? null : (
          <div className="menu-auth--opened">
            <div
              className={cn('menu-auth', {'menu-auth--hidden': !isOpenAuthMenu})}
            >
              {userProfile && !userProfile.block && <Navigation
                className="nav__mobile"
                onOpenMenu={handleCloseAuthMenu}
                roles={userProfile.roles}
              />}
              <MenuAuthUser/>
            </div>
          </div>
        )}

        {showAuthedNav && (
          <ProfileMenu
            isOpen={isOpenAuthMenu}
            locale={locale}
            roles={userProfile!.roles}
            allowedNavItems={allowedNavItems}
            allowedContentNavItems={allowedContentNavItems}
            onClickNavItem={handleClickNavItem}
            onClickContentNavItem={handleClickContentNavItem}
            onClickSettings={handleClickSettings}
            onSignOut={handleSignOut}
          />
        )}
        {showAuthedNav && (
          <MobileDrawer isOpen={isDrawerOpen} roles={userProfile!.roles} onClose={handleCloseDrawer}/>
        )}
      </div>
    </header>
  );
};
